import React, { useRef, useState } from 'react';
import { Card, Col, Form, Modal, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';

import FalconCardHeader from 'components/common/FalconCardHeader';
import Flex from 'components/common/Flex';
import IconButton from 'components/common/IconButton';
import DonorForm from 'components/donor/donor-form/DonorForm';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { useUpdateDonor } from 'hooks/useDonors';

const ProfileDonorInfo = ({ donorData }) => {
  const [showModal, setShowModal] = useState(false);

  const sectionRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null)
  ];

  const handleQuickUpdate = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const { id } = useParams();
  const { updateDonorAsync, isUpdating } = useUpdateDonor();

  const handleCreate = async data => {
    try {
      await updateDonorAsync({
        id,
        data
      });
      toast.success('Donor updated successfully');
      handleCloseModal();
    } catch (error) {
      toast.error('Something went wrong! Please try again');
      console.error(error);
    }
  };

  return (
    <>
      <Card className="mb-3">
        <FalconCardHeader
          title={<h4 className="fw-medium" style={{ fontSize: '1.25rem' }}>Donor information</h4>}
          light={false}
          titleTag="div"
          className="pb-1"
          menuClassName="align-items-start"
          endEl={
            <IconButton
              icon="pen"
              variant="falcon-default"
              transform="shrink-3"
              className="fs--1 bg-transparent shadow-none"
              onClick={handleQuickUpdate}
            >
              Quick Update
            </IconButton>
          }
        />

        <Card.Body>
          <Row>
            <Col>
              <Flex>
                <div className="donor-info-title">
                  <p className="mb-2 text-900" style={{ fontSize: '1rem' }}>Title</p>
                  <p className="mb-0 text-900" style={{ fontSize: '1rem' }}>Name</p>
                </div>
                <div>
                  <p className="mb-2" style={{ fontSize: '1rem' }}>{donorData?.title}</p>
                  <p className="text-primary h6" style={{ fontSize: '1rem' }}>
                    {donorData?.first_name +
                      ' ' +
                      donorData?.last_name}
                  </p>
                </div>
              </Flex>
              <hr />
              <Flex>
                <div className="donor-info-title">
                  <p className="text-900" style={{ fontSize: '1rem' }}>Address</p>
                </div>
                <div>
                  {(() => {
                    const addr = donorData?.address;
                    if (!addr) return '';
                    // Array format (app-created donors)
                    if (Array.isArray(addr) && addr.length > 0) {
                      const a = addr[0];
                      return (
                        <>
                          <p className="mb-2" style={{ fontSize: '1rem' }}>
                            {[a.addressLine1, a.addressLine2].filter(Boolean).join(', ')}
                          </p>
                          <p className="mb-0" style={{ fontSize: '1rem' }}>
                            {[a.city, a.country, a.postcode].filter(Boolean).join(', ')}
                          </p>
                        </>
                      );
                    }
                    // Object format (migrated donors)
                    if (typeof addr === 'object') {
                      return (
                        <>
                          <p className="mb-2" style={{ fontSize: '1rem' }}>
                            {[addr.street, addr.street2].filter(Boolean).join(', ')}
                          </p>
                          <p className="mb-0" style={{ fontSize: '1rem' }}>
                            {[addr.city, addr.state, addr.postcode, addr.country].filter(Boolean).join(', ')}
                          </p>
                        </>
                      );
                    }
                    return donorData?.primary_address || '';
                  })()}
                </div>
              </Flex>
              <hr />
              <Flex>
                <div className="donor-info-title">
                  <p className="text-900" style={{ fontSize: '1rem' }}>Phone No.</p>
                </div>
                <div>
                  {Array.isArray(donorData?.phone) && donorData.phone.length > 0
                    ? donorData.phone.map((phone, index) => {
                        const num = phone.phoneNumber || phone.number || '';
                        const isPrimary = phone.isPrimary !== undefined ? phone.isPrimary : phone.type === 'primary';
                        return (
                          <Flex key={index}>
                            <p className="mb-2" style={{ fontSize: '1rem' }}>{num ? '+' + num : ''}</p>
                            <Form.Group
                              className="ms-3"
                              controlId={`phone-control-${index}`}
                            >
                              <Flex>
                                <Form.Check
                                  inline
                                  type="radio"
                                  name="phone"
                                  id={`phone-control-${index}`}
                                  value={'phone'}
                                  checked={isPrimary}
                                  disabled
                                  className="me-2"
                                />
                                <Form.Label className="mb-0" style={{ fontSize: '1rem' }}>
                                  Primary
                                </Form.Label>
                              </Flex>
                            </Form.Group>
                          </Flex>
                        );
                      })
                    : donorData?.primary_phone
                      ? <p className="mb-2" style={{ fontSize: '1rem' }}>{donorData.primary_phone}</p>
                      : ''}
                </div>
              </Flex>
              <hr />
              <Flex>
                <div className="donor-info-title">
                  <p className="text-900" style={{ fontSize: '1rem' }}>E-Mail</p>
                </div>
                <div>
                  {Array.isArray(donorData?.email) && donorData.email.length > 0
                    ? donorData.email.map((email, index) => {
                        return (
                          <Flex key={index}>
                            <p className="mb-2" style={{ fontSize: '1rem' }}>{email.email}</p>
                            <Form.Group
                              className="ms-3"
                              controlId={`email-control-${index}`}
                            >
                              <Flex>
                                <Form.Check
                                  inline
                                  type="radio"
                                  name="email"
                                  id={`email-control-${index}`}
                                  value={'email'}
                                  checked={email.isPrimary}
                                  disabled
                                  className="me-2"
                                />
                                <Form.Label className="mb-0" style={{ fontSize: '1rem' }}>
                                  Primary
                                </Form.Label>
                              </Flex>
                            </Form.Group>
                          </Flex>
                        );
                      })
                    : donorData?.primary_email
                      ? <p className="mb-2" style={{ fontSize: '1rem' }}>{donorData.primary_email}</p>
                      : ''}
                </div>
              </Flex>
              <hr />
              {/* Donor Comment */}
              <Flex>
                <div className="donor-info-title">
                  <p className="mb-0 text-900" style={{ fontSize: '1rem' }}>Comment</p>
                </div>
                <div>
                  <p className="mb-0 text-primary" style={{ fontSize: '1rem' }}>{donorData?.comment}</p>
                </div>
              </Flex>
              <hr />
              {/* Donor Lead Source */}
              <Flex>
                <div className="donor-info-title">
                  <p className="mb-0 text-900" style={{ fontSize: '1rem' }}>Lead Source</p>
                </div>
                <div>
                  <p className="mb-0" style={{ fontSize: '1rem' }}>{donorData?.lead_source}</p>
                </div>
              </Flex>
              {donorData?.account_manager && (
                <>
                  <hr />
                  <Flex>
                    <div className="donor-info-title">
                      <p className="mb-0 text-900" style={{ fontSize: '1rem' }}>Account Manager</p>
                    </div>
                    <div>
                      <p className="mb-0 text-primary" style={{ fontSize: '1rem' }}>
                        {donorData.account_manager.first_name} {donorData.account_manager.last_name}
                      </p>
                    </div>
                  </Flex>
                </>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Update Donor Information</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <DonorForm
            sectionRefs={sectionRefs}
            loading={isUpdating}
            onSubmit={handleCreate}
            data={donorData}
            isEdit
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

ProfileDonorInfo.propTypes = {
  donorData: PropTypes.object
};

export default ProfileDonorInfo;
